import { ChangeEvent, Component } from "react";
import "./FolderMetadataDialog.css";
import "react-image-crop/dist/ReactCrop.css";
import Button from "components/ui/Button";
import Input from "./../../../../formElements/Input/Input";
import Textarea from "./../../../../formElements/Textarea/Textarea";
import Dialog from "components/ui/Dialog";
import LibraryObjectViewType from "../../../LibraryObjectCard/LibraryObjectViewType";
import LibraryObjectCard from "./../../../LibraryObjectCard/LibraryObjectCard";
import { blankLibraryObject, LibraryObject } from "services/content/model";
import { EDITOR_FILE_NAME } from "services/content/utils";
import { FolderForm, FormField } from "./folderForm";
import {
  createFile,
  createFolder,
  updateFolderMetadata,
  renameFolder,
} from "services/content/authoring";
import {
  Cropper,
  ImageCropperDialog,
  type CroppedImage,
} from "../ImageCropperDialog/ImageCropperDialog";
import { Translation } from "react-i18next";
import { dynamicKey } from "services/i18n";
import ApiErrorBanner from "components/ApiErrorBanner";

export enum FolderMetadataDialogType {
  CREATE = "create",
  METADATA = "metadata",
  NAME = "name",
  FILE = "file",
}

export enum PreviewType {
  REGULAR = "preview",
  VERTICAL = "preview_vertical",
  HORIZONTAL = "preview_horizontal",
}
interface ContentDataType {
  xhtml: Document;
  json: object;
}

interface ComponentProps {
  showDialog: boolean;
  onClose: (...args: unknown[]) => void;
  currentKey: string;
  libraryObject?: LibraryObject;
  form: FolderForm;
  type?: FolderMetadataDialogType;
  contentData?: ContentDataType;
  assetFiles?: File[];
  itemLabel?: string;
  refresh(): void;
}

interface ComponentState {
  object: LibraryObject;
  isFormSubmitted: boolean;
  errors: unknown;
  showSpinner: boolean;
  showPreviewVerticalSelectorDialog: boolean;
  showPreviewSelectorDialog: boolean;
  showPreviewHorizontalSelectorDialog: boolean;
  selectedFile: File | null;
  croppedFile: any;
  selectedFileData: string;
}

export default class FolderMetadataDialog extends Component<
  ComponentProps,
  ComponentState
> {
  state: ComponentState = {
    object: this.props.libraryObject,
    isFormSubmitted: false,
    errors: "",
    showSpinner: false,
    showPreviewVerticalSelectorDialog: false,
    showPreviewSelectorDialog: false,
    showPreviewHorizontalSelectorDialog: false,
    selectedFile: null,
    croppedFile: null,
  } as ComponentState;
  cropper: Cropper | null = null;
  type: FolderMetadataDialogType = this.props.type || FolderMetadataDialogType.CREATE;
  constructor(props: ComponentProps) {
    super(props);

    if (!this.props.libraryObject) {
      this.state.object = blankLibraryObject();
      this.state.object.order = -1;
    }

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleFieldChanged: (
    fieldName: string
  ) => (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void =
    (fieldName: string) => (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const object: LibraryObject = { ...this.state.object };
      if (fieldName === "name") {
        object.name = evt.target.value;
      } else if (fieldName === "author") {
        object.author = evt.target.value;
      } else if (fieldName === "description") {
        object.description = evt.target.value;
      } else if (fieldName === "order") {
        object.order = parseInt(evt.target.value, 10);
      } else if (fieldName === "length") {
        const value = parseInt(evt.target.value, 10);
        object.length = value < 0 ? 0 : value;
      } else if (fieldName === "readings") {
        const value = parseInt(evt.target.value, 10);
        object.readings = value < 0 ? 0 : value;
      } else if (fieldName === "videos") {
        const value = parseInt(evt.target.value, 10);
        object.videos = value < 0 ? 0 : value;
      }
      this.setState({ object: object });
    };

  isFormValid(): boolean {
    const object = this.state.object;
    const fields = this.props.form.fields;
    // Every field below `name` is optional on LibraryObject, so an unset one holds
    // undefined rather than null and a `!== null` test passes for an empty field.
    // Only `name` is required today, which is why nothing has depended on this.
    const filled = (value: unknown) => value !== undefined && value !== null;
    return (
      object.name !== "" &&
      !object.name.includes("/") &&
      (!fields.author.required || (filled(object.author) && object.author !== "")) &&
      (!fields.description.required ||
        (filled(object.description) && object.description !== "")) &&
      (!fields.order.required || (filled(object.order) && object.order !== -1)) &&
      (!fields.preview.required || filled(object.preview)) &&
      (!fields.preview_vertical.required || filled(object.preview_vertical)) &&
      (!fields.preview_horizontal.required || filled(object.preview_horizontal)) &&
      (!fields.readings.required || filled(object.readings)) &&
      (!fields.videos.required || filled(object.videos))
    );
  }

  getSimpleFileName(fileName: string | undefined): string {
    if (!fileName) {
      return "";
    }

    if (fileName.length <= 8) {
      return fileName;
    }

    const lastIndexOfSlash = fileName.lastIndexOf("/");
    const lastIndexOfDot = fileName.lastIndexOf(".");

    if (lastIndexOfSlash !== -1) {
      if (lastIndexOfDot - lastIndexOfSlash < 5) {
        return fileName.slice(lastIndexOfSlash + 1);
      }
      return `${fileName.slice(lastIndexOfSlash + 1, lastIndexOfSlash + 6)}...${fileName.slice(lastIndexOfDot, lastIndexOfDot + 4)}`;
    } else {
      return `${fileName.substring(0, 5)}...${fileName.substring(lastIndexOfDot)}`;
    }
  }

  handleSubmit(): void {
    this.setState({ isFormSubmitted: true, errors: "" });
    if (!this.isFormValid()) {
      if (this.state.object.name.includes("/")) {
        this.setState({ errors: "Name cannot contain '/' characters." });
      }
      return;
    }
    this.setState({ showSpinner: true });
    let submitPromise = null;
    if (this.type === FolderMetadataDialogType.METADATA) {
      submitPromise = updateFolderMetadata(this.state.object);
    } else if (this.type === FolderMetadataDialogType.NAME) {
      submitPromise = renameFolder(this.state.object);
    } else if (this.type === FolderMetadataDialogType.FILE) {
      if (!this.props.contentData) {
        this.setState({ errors: "No content data passed to this component." });
        return;
      }
      submitPromise = createFile(
        this.state.object,
        this.props.currentKey,
        [
          new File(
            [new XMLSerializer().serializeToString(this.props.contentData.xhtml)],
            `${this.state.object.name}.xhtml`,
            { type: "application/xhtml+xml" }
          ),
          new File([JSON.stringify(this.props.contentData.json)], EDITOR_FILE_NAME, {
            type: "application/json",
          }),
        ],
        this.props.assetFiles
      );
    } else {
      submitPromise = createFolder(this.state.object, this.props.currentKey);
    }
    submitPromise
      .then(() => {
        this.setState({ showSpinner: false });
        this.props.onClose();
        this.props.refresh();
      })
      .catch((errors: unknown) => {
        this.setState({ showSpinner: false, errors: errors });
      });
  }

  onCloseDialog = () => {
    this.setState({
      showPreviewVerticalSelectorDialog: false,
      showPreviewSelectorDialog: false,
      showPreviewHorizontalSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  openSelectorDialog(preview: PreviewType) {
    if (preview === PreviewType.REGULAR) {
      this.setState({
        showPreviewSelectorDialog: true,
      });
    } else if (preview === PreviewType.VERTICAL) {
      this.setState({
        showPreviewVerticalSelectorDialog: true,
      });
    } else {
      this.setState({
        showPreviewHorizontalSelectorDialog: true,
      });
    }
  }
  uploadPreview = (args: CroppedImage) => {
    const object: LibraryObject = { ...this.state.object };
    object.preview = args.file;
    object.preview_url = args.preview;
    this.setState({
      object: object,
      showPreviewSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  uploadPreviewVertical = (args: CroppedImage) => {
    const object: LibraryObject = { ...this.state.object };
    object.preview_vertical = args.file;
    object.preview_vertical_url = args.preview;
    this.setState({
      object: object,
      showPreviewVerticalSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  uploadPreviewHorizontal = (args: CroppedImage) => {
    const object: LibraryObject = { ...this.state.object };
    object.preview_horizontal = args.file;
    object.preview_horizontal_url = args.preview;
    this.setState({
      object: object,
      showPreviewHorizontalSelectorDialog: false,
      selectedFile: null,
      selectedFileData: "",
    });
  };
  onFileChangePreview = (
    event: ChangeEvent<HTMLInputElement>,
    previewType: PreviewType
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader: FileReader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== "string") {
        return;
      }
      const dataUrl = reader.result;
      const object: LibraryObject = { ...this.state.object };
      object[`${previewType}_url`] = dataUrl;
      this.setState({ object: object, selectedFileData: dataUrl });
    };
    reader.addEventListener("load", () =>
      this.setState({
        selectedFile: file,
      })
    );
    const object: LibraryObject = { ...this.state.object };
    object[previewType] = file;
    this.setState({
      object: object,
    });
    reader.readAsDataURL(file);
  };

  renderFieldLabel = (field: FormField) => {
    if (!field.required) {
      return <Translation>{t => t(dynamicKey("Learn." + field.label))}</Translation>;
    }

    return (
      <Translation>{t => <>* {t(dynamicKey("Learn." + field.label))}</>}</Translation>
    );
  };

  // The kit ✕/Escape call this with no argument — never forward their event.
  handleClose = () => {
    this.props.onClose();
  };

  render() {
    // At the content root the next level is a Source, not a Folder.
    const titleKey =
      this.type === FolderMetadataDialogType.FILE
        ? "Learn.folderDialog.finalDetails"
        : this.props.form.kind === "source"
          ? "Learn.folderDialog.addSource"
          : "Learn.folderDialog.addFolder";

    return (
      <Translation>
        {t => (
          <Dialog
            className="admin-form-dialog folder-metadata"
            size="lg"
            open={this.props.showDialog}
            onClose={this.handleClose}
            closeOnOverlayClick={false}
            title={t(titleKey)}
            secondaryAction={
              <Button
                size="small"
                variant="secondary"
                className="cancel"
                onClick={this.handleClose}
                disabled={this.state.showSpinner}
              >
                {t("Common.actions.cancel")}
              </Button>
            }
            mainAction={
              <Button
                size="small"
                variant="primary"
                className="submit"
                onClick={this.handleSubmit}
                disabled={this.state.showSpinner}
              >
                {t("Common.Save")}
              </Button>
            }
            loading={this.state.showSpinner}
          >
            <ApiErrorBanner error={this.state.errors} />
            <div className="form-fields">
              <div>
                <label htmlFor="folder-name">
                  {this.renderFieldLabel(this.props.form.fields.name)}{" "}
                  {this.props.itemLabel ? " of " + this.props.itemLabel : ""}
                </label>
              </div>
              <div className="width-100">
                <Input
                  name="folder-name"
                  id="folder-name"
                  type="text"
                  value={this.state.object.name}
                  onChange={this.handleFieldChanged("name")}
                  required={this.props.form.fields.name.required}
                  formSubmitted={this.state.isFormSubmitted}
                  maxLength={this.props.form.fields.name.maxLength}
                  disabled={this.type === FolderMetadataDialogType.METADATA}
                />
              </div>
              {this.props.form.fields.author.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-author">
                      {this.renderFieldLabel(this.props.form.fields.author)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-author"
                      id="folder-author"
                      type="text"
                      value={this.state.object.author || ""}
                      onChange={this.handleFieldChanged("author")}
                      required={this.props.form.fields.author.required}
                      formSubmitted={this.state.isFormSubmitted}
                      maxLength={this.props.form.fields.author.maxLength}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {this.props.form.fields.description.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-description">
                      {this.renderFieldLabel(this.props.form.fields.description)}
                    </label>
                  </div>
                  <div>
                    <Textarea
                      name="folder-description"
                      id="folder-description"
                      value={this.state.object.description || ""}
                      required={this.props.form.fields.description.required}
                      formSubmitted={this.state.isFormSubmitted}
                      onChange={this.handleFieldChanged("description")}
                      maxLength={this.props.form.fields.description.maxLength}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {this.props.form.fields.order.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-order">
                      {this.renderFieldLabel(this.props.form.fields.order)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-order"
                      id="folder-order"
                      type="number"
                      value={this.state.object.order ?? ""}
                      onChange={this.handleFieldChanged("order")}
                      required={this.props.form.fields.order.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {this.props.form.fields.length.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-order">
                      {this.renderFieldLabel(this.props.form.fields.length)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-length"
                      id="folder-length"
                      type="number"
                      min={0}
                      value={this.state.object.length ?? ""}
                      onChange={this.handleFieldChanged("length")}
                      required={this.props.form.fields.length.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {this.props.form.fields.readings.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-readings">
                      {this.renderFieldLabel(this.props.form.fields.readings)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-readings"
                      id="folder-readings"
                      type="number"
                      min={0}
                      value={this.state.object.readings ?? ""}
                      onChange={this.handleFieldChanged("readings")}
                      required={this.props.form.fields.readings.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              {this.props.form.fields.videos.isVisible && (
                <>
                  <div>
                    <label htmlFor="folder-videos">
                      {this.renderFieldLabel(this.props.form.fields.videos)}
                    </label>
                  </div>
                  <div>
                    <Input
                      name="folder-videos"
                      id="folder-videos"
                      type="number"
                      min={0}
                      value={this.state.object.videos ?? ""}
                      onChange={this.handleFieldChanged("videos")}
                      required={this.props.form.fields.videos.required}
                      formSubmitted={this.state.isFormSubmitted}
                      disabled={this.type === FolderMetadataDialogType.NAME}
                    />
                  </div>
                </>
              )}
              <div style={{ display: "flex" }}>
                {this.props.form.fields.preview.isVisible &&
                  this.type !== FolderMetadataDialogType.NAME && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginRight: 10,
                      }}
                    >
                      <div>
                        <label htmlFor="folder-preview">
                          {this.renderFieldLabel(this.props.form.fields.preview)}
                        </label>
                        {this.state.isFormSubmitted &&
                          this.props.form.fields.preview.required &&
                          !this.state.object.preview && (
                            <div className="input-container">
                              <div className="input-tooltip required width-100">
                                * Required field
                              </div>
                            </div>
                          )}
                      </div>
                      <div>
                        <Button
                          variant="plain"
                          style={{ marginTop: "10px", color: "var(--action-brand)" }}
                          onClick={() => this.openSelectorDialog(PreviewType.REGULAR)}
                        >
                          Upload{" "}
                          {this.getSimpleFileName(
                            this.state.object.preview?.name ||
                              this.state.object.preview_url
                          ) || " No File Chosen"}
                        </Button>
                        {this.state.showPreviewSelectorDialog && (
                          <ImageCropperDialog
                            file={this.state.selectedFile}
                            selectedFileData={this.state.selectedFileData}
                            showDialog={this.state.showPreviewSelectorDialog}
                            onClose={this.onCloseDialog}
                            aspect={1}
                            onFileChange={event =>
                              this.onFileChangePreview(event, PreviewType.REGULAR)
                            }
                            onSubmitPreview={this.uploadPreview}
                          />
                        )}
                      </div>
                    </div>
                  )}
                {this.props.form.fields.preview_vertical.isVisible &&
                  this.type !== FolderMetadataDialogType.NAME && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginRight: 10,
                      }}
                    >
                      <div>
                        <label htmlFor="folder-preview">
                          {this.renderFieldLabel(this.props.form.fields.preview_vertical)}
                        </label>
                        {this.state.isFormSubmitted &&
                          this.props.form.fields.preview_vertical.required &&
                          !this.state.object.preview_vertical && (
                            <div className="input-container">
                              <div className="input-tooltip required width-100">
                                * Required field
                              </div>
                            </div>
                          )}
                      </div>
                      <div>
                        <Button
                          variant="plain"
                          style={{ marginTop: "10px", color: "var(--action-brand)" }}
                          onClick={() => this.openSelectorDialog(PreviewType.VERTICAL)}
                        >
                          Upload{" "}
                          {this.getSimpleFileName(
                            this.state.object.preview_vertical?.name ||
                              this.state.object.preview_vertical_url
                          ) || " No File Chosen"}
                        </Button>
                        {this.state.showPreviewVerticalSelectorDialog && (
                          <ImageCropperDialog
                            file={this.state.selectedFile}
                            selectedFileData={this.state.selectedFileData}
                            showDialog={this.state.showPreviewVerticalSelectorDialog}
                            onClose={this.onCloseDialog}
                            aspect={9 / 16}
                            onFileChange={event =>
                              this.onFileChangePreview(event, PreviewType.VERTICAL)
                            }
                            onSubmitPreview={this.uploadPreviewVertical}
                          />
                        )}
                      </div>
                    </div>
                  )}
                {this.props.form.fields.preview_horizontal.isVisible &&
                  this.type !== FolderMetadataDialogType.NAME && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        marginRight: 10,
                      }}
                    >
                      <div>
                        <label htmlFor="folder-preview">
                          {this.renderFieldLabel(
                            this.props.form.fields.preview_horizontal
                          )}
                        </label>
                        {this.state.isFormSubmitted &&
                          this.props.form.fields.preview_horizontal.required &&
                          !this.state.object.preview_horizontal && (
                            <div className="input-container">
                              <div className="input-tooltip required width-100">
                                * Required field
                              </div>
                            </div>
                          )}
                      </div>
                      <div>
                        <Button
                          variant="plain"
                          style={{ marginTop: "10px", color: "var(--action-brand)" }}
                          onClick={() => this.openSelectorDialog(PreviewType.HORIZONTAL)}
                        >
                          Upload{" "}
                          {this.getSimpleFileName(
                            this.state.object.preview_horizontal?.name ||
                              this.state.object.preview_horizontal_url
                          ) || " No File Chosen"}
                        </Button>
                        {this.state.showPreviewHorizontalSelectorDialog && (
                          <ImageCropperDialog
                            file={this.state.selectedFile}
                            selectedFileData={this.state.selectedFileData}
                            showDialog={this.state.showPreviewHorizontalSelectorDialog}
                            aspect={16 / 9}
                            onClose={this.onCloseDialog}
                            onFileChange={event =>
                              this.onFileChangePreview(event, PreviewType.HORIZONTAL)
                            }
                            onSubmitPreview={this.uploadPreviewHorizontal}
                          />
                        )}
                      </div>
                    </div>
                  )}
              </div>
              {this.props.form.fields.preview.isVisible &&
                this.state.object.preview_url && (
                  <div>
                    <h4>{t("Common.actions.preview")}</h4>
                    <LibraryObjectCard
                      object={this.state.object}
                      type={LibraryObjectViewType.LIST}
                      hasLesson={true}
                      ribbon={"Square"}
                    />
                  </div>
                )}
              {this.props.form.fields.preview_vertical.isVisible &&
                this.state.object.preview_vertical_url && (
                  <div>
                    <h4>{t("Common.actions.preview")}</h4>
                    <LibraryObjectCard
                      object={this.state.object}
                      type={LibraryObjectViewType.LIST}
                      hasLesson={false}
                      ribbon={"Vertical"}
                    />
                  </div>
                )}
              {this.props.form.fields.preview_vertical.isVisible &&
                this.state.object.preview_horizontal_url && (
                  <div>
                    <h4>{t("Common.actions.preview")}</h4>
                    <LibraryObjectCard
                      object={this.state.object}
                      type={LibraryObjectViewType.LIST}
                      asFolder
                      ribbon={"Horizontal"}
                    />
                  </div>
                )}
            </div>
          </Dialog>
        )}
      </Translation>
    );
  }
}
